import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { resolveQuizType } from "@/lib/analytics/quiz-analytics";

import { portableTextToPlain } from "@/utils/portableText.util";

import { clearTemporaryBasket } from "@/app/blu/(checkout-flow)/actions";
import { QUIZ_DOCUMENT_ID } from "@/constants/page.constants";
import { useTagging } from "@/hooks/useTagging";

import {
  addQuizProductsToCart,
  clearQuizAnswersCookie,
  setQuizAnswersCookie,
} from "../actions";
import {
  FlatQuestion,
  QuizData,
  QuizOptionData,
} from "../components/quizFlow.types";
import {
  buildQuizCartPayload,
  getMonitoringOverride,
} from "../utils/quizCartPayload";
import {
  clearQuizData,
  getQuizAnswers,
  getQuizHistory,
  getSavedQuestionKey,
  markLeadCaptureSubmitted,
  markQuizCompleted,
  QuizAnswers,
  saveCurrentQuestionKey,
  saveQuizAnswers,
  saveQuizHistory,
} from "../utils/quizStorage";

export const useQuizFlow = ({
  quiz,
  startQuestionKey,
  source,
  onFinish,
}: UseQuizFlowParams) => {
  // Namespaces saved progress in browser storage.
  const quizId = quiz.id ?? QUIZ_DOCUMENT_ID;
  const [, setTag] = useTagging();

  const { flatQuestions, questionMap } = useMemo(() => {
    const flat: FlatQuestion[] = [];
    const map = new Map<string, FlatQuestion>();

    (quiz.steps ?? []).forEach((step, stepIndex) => {
      (step.questions ?? []).forEach((q) => {
        const fq: FlatQuestion = { ...q, stepIndex };
        flat.push(fq);
        map.set(q.questionKey, fq);
      });
    });

    return { flatQuestions: flat, questionMap: map };
  }, [quiz]);

  const fallbackQuestion = useMemo(() => {
    if (startQuestionKey && questionMap.has(startQuestionKey)) {
      return startQuestionKey;
    }
    return flatQuestions[0]?.questionKey ?? "";
  }, [startQuestionKey, questionMap, flatQuestions]);

  const [currentQuestionKey, setCurrentQuestionKey] =
    useState(fallbackQuestion);
  const [history, setHistory] = useState<string[]>([]);
  const [answers, setAnswers] = useState<QuizAnswers>({});
  const [isFinished, setIsFinished] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const selectedProductIds = useRef<string[]>([]);

  const [isRehydrated, setIsRehydrated] = useState(false);

  const quizType = useMemo((): string => {
    const firstQuestion = flatQuestions[0];
    const firstAnswer = firstQuestion
      ? answers[firstQuestion.questionKey]
      : undefined;
    return resolveQuizType({ startQuestionKey, answer: firstAnswer });
  }, [startQuestionKey, flatQuestions, answers]);

  // Rehydrate from localStorage once
  useEffect(() => {
    const restoredState = getQuizBrowserHistoryState(quizId, questionMap);
    if (restoredState) {
      setCurrentQuestionKey(restoredState.currentQuestionKey);
      setHistory(restoredState.history);
      setAnswers(restoredState.answers);
      setIsRehydrated(true);
      return;
    }

    if (startQuestionKey && questionMap.has(startQuestionKey)) {
      setCurrentQuestionKey(startQuestionKey);
      setHistory([]);
      setAnswers({});
      setIsRehydrated(true);
      return;
    }

    const savedKey = getSavedQuestionKey(quizId);
    const savedHistory = getQuizHistory(quizId);
    const savedAnswers = getQuizAnswers(quizId);

    if (savedKey && questionMap.has(savedKey)) {
      setCurrentQuestionKey(savedKey);
    }
    if (savedHistory.length > 0) {
      setHistory(savedHistory);
    }
    if (savedAnswers && Object.keys(savedAnswers).length > 0) {
      setAnswers(savedAnswers);
    }

    setIsRehydrated(true);
  }, []);

  useEffect(() => {
    if (!isRehydrated || !currentQuestionKey) return;
    saveQuizBrowserHistoryState(quizId, {
      currentQuestionKey,
      history,
      answers,
    });
  }, [answers, currentQuestionKey, history, isRehydrated, quizId]);

  const currentQuestion = questionMap.get(currentQuestionKey);
  const currentStepIndex = currentQuestion?.stepIndex ?? 0;

  const buildCartSubmission = useCallback(
    (latestAnswers: QuizAnswers) => {
      const kitId = quiz.kitProduct?.id;
      if (!kitId) {
        return null;
      }
      const { items } = buildQuizCartPayload(latestAnswers, flatQuestions);
      const monitoringOverrideId = getMonitoringOverride(latestAnswers);
      return { kitId, items, monitoringOverrideId };
    },
    [quiz.kitProduct?.id, flatQuestions],
  );

  const hasFiredCompleteRef = useRef(false);
  useEffect(() => {
    if (!isRehydrated || hasFiredCompleteRef.current) return;
    if (currentQuestion?.answerType === "leadCapture") {
      setTag({ event: "quiz_complete_event", "Quiz-Type": quizType });
      hasFiredCompleteRef.current = true;

      const submission = buildCartSubmission(answers);
      if (submission) {
        setIsLoading(true);
        addQuizProductsToCart(
          submission.kitId,
          submission.items,
          submission.monitoringOverrideId,
        ).finally(() => setIsLoading(false));
      }
    }
  }, [
    isRehydrated,
    currentQuestion,
    quizType,
    setTag,
    buildCartSubmission,
    answers,
  ]);

  // Persist answers to localStorage only after rehydration
  useEffect(() => {
    if (!isRehydrated) return;
    if (Object.keys(answers).length > 0) {
      saveQuizAnswers(quizId, answers);
    }
  }, [answers, quizId, isRehydrated]);

  // Persist current question key only after rehydration
  useEffect(() => {
    if (!isRehydrated) return;
    if (currentQuestionKey) {
      saveCurrentQuestionKey(quizId, currentQuestionKey);
    }
  }, [currentQuestionKey, quizId, isRehydrated]);

  // Persist history only after rehydration
  useEffect(() => {
    if (!isRehydrated) return;
    saveQuizHistory(quizId, history);
  }, [history, quizId, isRehydrated]);

  const resolveNextKey = useCallback(
    (
      question: FlatQuestion,
      selectedOption?: QuizOptionData,
    ): string | null => {
      if (selectedOption?.nextQuestionKey) {
        return selectedOption.nextQuestionKey;
      }
      if (question.nextQuestionKey) {
        return question.nextQuestionKey;
      }
      const currentIndex = flatQuestions.findIndex(
        (q) => q.questionKey === question.questionKey,
      );
      if (currentIndex < flatQuestions.length - 1) {
        return flatQuestions[currentIndex + 1].questionKey;
      }
      return null;
    },
    [flatQuestions],
  );

  const navigateToQuestion = useCallback(
    (nextKey: string | null) => {
      if (!nextKey || !questionMap.has(nextKey)) {
        setIsFinished(true);
        return;
      }
      setHistory((prev) => [...prev, currentQuestionKey]);
      setCurrentQuestionKey(nextKey);
    },
    [currentQuestionKey, questionMap],
  );

  const handleSingleChoiceSelect = useCallback(
    (optionKey: string) => {
      if (!currentQuestion) return;

      const selectedOption = currentQuestion.options?.find(
        (o) =>
          o._key === optionKey ||
          o.title === optionKey ||
          o.product?.id === optionKey,
      );
      const answerValue =
        selectedOption?.product?.id ?? selectedOption?.title ?? optionKey;
      setAnswers((prev) => ({
        ...prev,
        [currentQuestion.questionKey]: answerValue,
      }));

      if (selectedOption?.product?.id) {
        selectedProductIds.current = [
          ...selectedProductIds.current.filter(
            (id) => !currentQuestion.options?.some((o) => o.product?.id === id),
          ),
          selectedOption.product.id,
        ];
      }

      const stepOffset = source === "hp" ? 1 : 0;
      const stepNumber = history.length + 1 + stepOffset;
      const questionText = truncateAtQuestionMark(
        portableTextToPlain(currentQuestion.title),
      );
      const eventQuizType =
        resolveQuizType({ startQuestionKey, answer: answerValue }) || quizType;

      if (history.length === 0 && source !== "hp") {
        setTag({
          event: "quiz_start_event",
          "Quiz-Type": eventQuizType,
          "Quiz-Step": String(stepNumber),
          "Quiz-Question": questionText,
          "Quiz-Answers": formatAnswer(answerValue),
        });
      }
      setTag({
        event: "quiz_event",
        "Quiz-Type": eventQuizType,
        "Quiz-Step": String(stepNumber),
        "Quiz-Question": questionText,
        "Quiz-Answers": formatAnswer(answerValue),
      });

      const nextKey = resolveNextKey(currentQuestion, selectedOption);
      navigateToQuestion(nextKey);
    },
    [
      currentQuestion,
      history.length,
      source,
      startQuestionKey,
      quizType,
      setTag,
      resolveNextKey,
      navigateToQuestion,
    ],
  );

  const handleMultipleChoiceNext = useCallback(
    (selectedKeys: string[]) => {
      if (!currentQuestion) return;
      const selectedOptions = selectedKeys.map((sk) => {
        const opt = currentQuestion.options?.find(
          (o) => o._key === sk || o.title === sk || o.product?.id === sk,
        );
        return opt ?? null;
      });

      const answerValues = selectedOptions.map((opt, i) => {
        if (!opt) return selectedKeys[i];
        if (opt.product?.id) return opt.product.id;
        return opt.title ?? selectedKeys[i];
      });

      setAnswers((prev) => ({
        ...prev,
        [currentQuestion.questionKey]: answerValues,
      }));

      const questionOptionIds = new Set(
        currentQuestion.options?.map((o) => o.product?.id).filter(Boolean),
      );
      const newProductIds = selectedOptions
        .map((opt) => opt?.product?.id)
        .filter((id): id is string => Boolean(id));
      selectedProductIds.current = [
        ...selectedProductIds.current.filter(
          (id) => !questionOptionIds.has(id),
        ),
        ...newProductIds,
      ];

      const stepOffset = source === "hp" ? 1 : 0;
      const stepNumber = history.length + 1 + stepOffset;
      const questionText = truncateAtQuestionMark(
        portableTextToPlain(currentQuestion.title),
      );

      if (history.length === 0 && source !== "hp") {
        setTag({
          event: "quiz_start_event",
          "Quiz-Type": quizType,
          "Quiz-Step": String(stepNumber),
          "Quiz-Question": questionText,
          "Quiz-Answers": formatAnswer(answerValues),
        });
      }
      setTag({
        event: "quiz_event",
        "Quiz-Type": quizType,
        "Quiz-Step": String(stepNumber),
        "Quiz-Question": questionText,
        "Quiz-Answers": formatAnswer(answerValues),
      });

      const nextKey = resolveNextKey(currentQuestion);
      navigateToQuestion(nextKey);
    },
    [
      currentQuestion,
      history.length,
      source,
      quizType,
      setTag,
      resolveNextKey,
      navigateToQuestion,
    ],
  );

  const handleNumberInputNext = useCallback(
    (value: number) => {
      if (!currentQuestion) return;
      setAnswers((prev) => ({
        ...prev,
        [currentQuestion.questionKey]: value,
      }));

      const stepOffset = source === "hp" ? 1 : 0;
      const stepNumber = history.length + 1 + stepOffset;
      const questionText = truncateAtQuestionMark(
        portableTextToPlain(currentQuestion.title),
      );

      if (history.length === 0 && source !== "hp") {
        setTag({
          event: "quiz_start_event",
          "Quiz-Type": quizType,
          "Quiz-Step": String(stepNumber),
          "Quiz-Question": questionText,
          "Quiz-Answers": formatAnswer(value),
        });
      }
      setTag({
        event: "quiz_event",
        "Quiz-Type": quizType,
        "Quiz-Step": String(stepNumber),
        "Quiz-Question": questionText,
        "Quiz-Answers": formatAnswer(value),
      });

      const nextKey = resolveNextKey(currentQuestion);
      navigateToQuestion(nextKey);
    },
    [
      currentQuestion,
      history.length,
      source,
      quizType,
      setTag,
      resolveNextKey,
      navigateToQuestion,
    ],
  );

  const handleLeadCaptureSubmit = useCallback(
    async (data: { email: string; phone: string }) => {
      if (!currentQuestion) return;
      const nextAnswers: QuizAnswers = {
        ...answers,
        [currentQuestion.questionKey]: data,
      };
      setAnswers(nextAnswers);
      setIsLoading(true);
      markLeadCaptureSubmitted(quizId);
      await setQuizAnswersCookie(quizId, nextAnswers);
      setIsFinished(true);
      markQuizCompleted(quizId);
      onFinish?.();
    },
    [currentQuestion, onFinish, quizId, answers],
  );

  const handleLeadCaptureSkip = useCallback(async () => {
    setIsLoading(true);
    await setQuizAnswersCookie(quizId, answers);
    setIsFinished(true);
    markQuizCompleted(quizId);
    onFinish?.();
  }, [onFinish, quizId, answers]);

  const handleBack = useCallback(() => {
    if (history.length === 0) return;
    const prevKey = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setIsFinished(false);
    setCurrentQuestionKey(prevKey);
  }, [history]);

  const stepLabels = useMemo(
    () => (quiz.steps ?? []).map((s) => s.label),
    [quiz.steps],
  );

  const finishStepIndex = (quiz.steps ?? []).length - 1;
  const canGoBack = history.length > 0;

  const resetQuiz = useCallback(() => {
    clearQuizData(quizId);
    clearQuizAnswersCookie(quizId);
    clearTemporaryBasket();
    setCurrentQuestionKey(fallbackQuestion);
    setHistory([]);
    setAnswers({});
    setIsFinished(false);
    setIsLoading(false);
  }, [quizId, fallbackQuestion]);

  return {
    quizId,
    currentQuestion,
    currentStepIndex,
    isFinished,
    isLoading,
    isRehydrated,
    answers,
    stepLabels,
    finishStepIndex,
    canGoBack,
    handleSingleChoiceSelect,
    handleMultipleChoiceNext,
    handleNumberInputNext,
    handleLeadCaptureSubmit,
    handleLeadCaptureSkip,
    handleBack,
    resetQuiz,
  };
};

type UseQuizFlowParams = {
  quiz: QuizData;
  startQuestionKey?: string;
  source?: string;
  onFinish?: () => void;
};

type QuizBrowserHistoryState = {
  quizId: string;
  currentQuestionKey: string;
  history: string[];
  answers: QuizAnswers;
};

function getQuizBrowserHistoryState(
  quizId: string,
  questionMap: Map<string, FlatQuestion>,
): QuizBrowserHistoryState | null {
  if (typeof window === "undefined") return null;
  const state = window.history.state as
    | { quizFlow?: Partial<QuizBrowserHistoryState> }
    | null
    | undefined;
  const quizFlow = state?.quizFlow;
  if (
    quizFlow?.quizId !== quizId ||
    !quizFlow.currentQuestionKey ||
    !questionMap.has(quizFlow.currentQuestionKey)
  ) {
    return null;
  }
  const history = Array.isArray(quizFlow.history)
    ? quizFlow.history.filter((key) => questionMap.has(key))
    : [];
  return {
    quizId,
    currentQuestionKey: quizFlow.currentQuestionKey,
    history,
    answers: quizFlow.answers ?? {},
  };
}

function saveQuizBrowserHistoryState(
  quizId: string,
  state: Omit<QuizBrowserHistoryState, "quizId">,
) {
  if (typeof window === "undefined") return;
  const currentState =
    typeof window.history.state === "object" && window.history.state !== null
      ? window.history.state
      : {};
  window.history.replaceState(
    {
      ...currentState,
      quizFlow: {
        quizId,
        ...state,
      },
    },
    "",
  );
}

function formatAnswer(
  answer: string | string[] | number | { email: string; phone: string },
): string {
  if (Array.isArray(answer)) return answer.join(" | ");
  if (typeof answer === "object" && answer !== null) return answer.email;
  return String(answer);
}

function truncateAtQuestionMark(text: string): string {
  const idx = text.indexOf("?");
  if (idx === -1) return text;
  return text.slice(0, idx + 1);
}
